import * as THREE from "three";
import {
  calculateSuntraceMotionLoad,
  stepSuntraceSpring,
  type SuntraceMotionLoadProfile,
  type SuntraceMotionSnapshot,
  type SuntraceSpringState,
} from "./motion";
import type { TrackFrame } from "./track";
import type { RacePresentationSnapshot } from "./types";
import type { SuntraceCameraMode } from "./types";

const damp = (current: number, target: number, smoothing: number, delta: number): number =>
  THREE.MathUtils.lerp(current, target, 1 - Math.exp(-delta / smoothing));

const TAU = Math.PI * 2;
const MAX_TRACK_GRADE_SINE = 0.6;
const LANDSCAPE_DOWNWARD_PITCH = Math.tan(THREE.MathUtils.degToRad(3.4));

const finiteOr = (value: number, fallback = 0): number =>
  Number.isFinite(value) ? value : fallback;

const finiteClamp = (value: number, minimum: number, maximum: number): number =>
  THREE.MathUtils.clamp(finiteOr(value), minimum, maximum);

export type ChaseCameraLoadProfile = SuntraceMotionLoadProfile & {
  cornerLoad: number;
  anticipation: number;
  battleOffset: number;
  battleLoad: number;
};

export type ChaseCameraMotionResponse = {
  lateralOffset: number;
  rollRadians: number;
  targetHeight: number;
  boostLag: number;
};

const getChaseCameraMotionResponse = (
  profile: SuntraceMotionLoadProfile,
  target: ChaseCameraMotionResponse,
  impact = 0,
  impactSide = 0,
): ChaseCameraMotionResponse => {
  target.lateralOffset = THREE.MathUtils.clamp(
    -profile.lateralLoad * 0.48 * profile.motionScale,
    -0.48,
    0.48,
  );
  target.rollRadians = THREE.MathUtils.clamp(
    (
      -profile.lateralLoad * 0.024 +
      finiteClamp(impact, 0, 1) * finiteClamp(impactSide, -1, 1) * 0.024
    ) * profile.motionScale,
    -0.024,
    0.024,
  );
  target.targetHeight = THREE.MathUtils.clamp(
    profile.verticalLoad * 0.1 * profile.motionScale,
    -0.1,
    0.1,
  );
  target.boostLag = THREE.MathUtils.clamp(
    profile.boostLoad * 0.28 * profile.motionScale,
    0,
    0.28,
  );
  return target;
};

export const calculateChaseCameraMotionResponse = (
  snapshot: SuntraceMotionSnapshot,
  reducedMotion: boolean,
  target: ChaseCameraMotionResponse = {
    lateralOffset: 0,
    rollRadians: 0,
    targetHeight: 0,
    boostLag: 0,
  },
): ChaseCameraMotionResponse => getChaseCameraMotionResponse(
  calculateSuntraceMotionLoad(snapshot, reducedMotion),
  target,
  snapshot.impact,
  snapshot.impactSide,
);

export type ChaseCameraMotionController = {
  update: (
    snapshot: RacePresentationSnapshot,
    deltaSeconds: number,
    reducedMotion: boolean,
  ) => ChaseCameraMotionResponse;
  reset: () => void;
};

export const createChaseCameraMotionController = (): ChaseCameraMotionController => {
  const response: ChaseCameraMotionResponse = {
    lateralOffset: 0,
    rollRadians: 0,
    targetHeight: 0,
    boostLag: 0,
  };
  const target: ChaseCameraMotionResponse = { ...response };
  const lateralOffsetSpring: SuntraceSpringState = { value: 0, velocity: 0 };
  const rollSpring: SuntraceSpringState = { value: 0, velocity: 0 };
  const targetHeightSpring: SuntraceSpringState = { value: 0, velocity: 0 };
  const boostLagSpring: SuntraceSpringState = { value: 0, velocity: 0 };
  let previousSpeed: number | null = null;

  const resetSpring = (state: SuntraceSpringState): void => {
    state.value = 0;
    state.velocity = 0;
  };

  const stepSpring = (
    state: SuntraceSpringState,
    springTarget: number,
    stiffness: number,
    minimum: number,
    maximum: number,
    delta: number,
  ): void => {
    stepSuntraceSpring(state, springTarget, stiffness, 1.05, delta);
    state.value = finiteClamp(state.value, minimum, maximum);
  };

  const reset = (): void => {
    resetSpring(lateralOffsetSpring);
    resetSpring(rollSpring);
    resetSpring(targetHeightSpring);
    resetSpring(boostLagSpring);
    response.lateralOffset = 0;
    response.rollRadians = 0;
    response.targetHeight = 0;
    response.boostLag = 0;
    previousSpeed = null;
  };

  return {
    update(snapshot, deltaSeconds, reducedMotion) {
      const delta = finiteClamp(deltaSeconds, 0, 0.05);
      if (delta === 0) {
        return response;
      }
      const speed = finiteClamp(snapshot.speed, 0, 110);
      const acceleration = previousSpeed === null
        ? 0
        : finiteClamp((speed - previousSpeed) / delta, -12, 12);
      previousSpeed = speed;
      calculateChaseCameraMotionResponse(
        { ...snapshot, speed, acceleration },
        reducedMotion,
        target,
      );
      stepSpring(
        lateralOffsetSpring,
        target.lateralOffset,
        52,
        -0.48,
        0.48,
        delta,
      );
      stepSpring(
        rollSpring,
        target.rollRadians,
        72,
        -0.024,
        0.024,
        delta,
      );
      stepSpring(
        targetHeightSpring,
        target.targetHeight,
        42,
        -0.1,
        0.1,
        delta,
      );
      stepSpring(
        boostLagSpring,
        target.boostLag,
        34,
        0,
        0.28,
        delta,
      );
      response.lateralOffset = lateralOffsetSpring.value;
      response.rollRadians = rollSpring.value;
      response.targetHeight = targetHeightSpring.value;
      response.boostLag = boostLagSpring.value;
      return response;
    },
    reset,
  };
};

const getWrappedProgressDelta = (from: number, to: number): number => {
  const delta = to - from;
  return delta > 0.5 ? delta - 1 : delta < -0.5 ? delta + 1 : delta;
};

const normalizeTrackProgress = (progress: number): number | null =>
  Number.isFinite(progress) ? THREE.MathUtils.euclideanModulo(progress, 1) : null;

const getBattleFraming = (
  snapshot: RacePresentationSnapshot,
  target: ChaseCameraLoadProfile,
): void => {
  let strongestLoad = 0;
  let strongestOffset = 0;
  const playerProgress = normalizeTrackProgress(snapshot.trackProgress) ?? 0;
  const playerLateralOffset = Number.isFinite(snapshot.lateralOffset)
    ? snapshot.lateralOffset
    : 0;

  for (const rival of snapshot.rivals) {
    if (rival.visible === false) {
      continue;
    }

    const rivalProgress = normalizeTrackProgress(rival.trackProgress);
    if (rivalProgress === null || !Number.isFinite(rival.lateralOffset)) {
      continue;
    }

    const gap = getWrappedProgressDelta(playerProgress, rivalProgress);
    const lateralDifference = rival.lateralOffset - playerLateralOffset;

    if (gap <= 0.0018 || gap >= 0.026 || Math.abs(lateralDifference) < 1) {
      continue;
    }

    const safeNearField = THREE.MathUtils.smoothstep(gap, 0.0018, 0.0044);
    const distanceFalloff = 1 - THREE.MathUtils.smoothstep(gap, 0.011, 0.026);
    const lateralReadiness = THREE.MathUtils.smoothstep(
      Math.abs(lateralDifference),
      1.25,
      2.2
    );
    const load = safeNearField * distanceFalloff * lateralReadiness;

    if (load <= strongestLoad) {
      continue;
    }

    strongestLoad = load;
    strongestOffset = THREE.MathUtils.clamp(lateralDifference * 0.15, -0.46, 0.46) * load;
  }

  target.battleOffset = strongestOffset;
  target.battleLoad = strongestLoad;
};

export const calculateChaseCameraLoadProfile = (
  snapshot: RacePresentationSnapshot,
  reducedMotion: boolean,
  target: ChaseCameraLoadProfile = {
    speedLoad: 0,
    lateralLoad: 0,
    verticalLoad: 0,
    boostLoad: 0,
    sprayLoad: 0,
    cornerLoad: 0,
    anticipation: 0,
    battleOffset: 0,
    battleLoad: 0,
    motionScale: 1,
  },
): ChaseCameraLoadProfile => {
  calculateSuntraceMotionLoad(snapshot, reducedMotion, target);
  target.anticipation = target.lateralLoad;
  getBattleFraming(snapshot, target);
  return target;
};

export type SuntraceChaseCamera = {
  camera: THREE.PerspectiveCamera;
  update: (
    snapshot: RacePresentationSnapshot,
    frame: TrackFrame,
    deltaSeconds: number,
    reducedMotion: boolean,
  ) => void;
  resize: (width: number, height: number) => void;
  reset: (frame: TrackFrame) => void;
  cycleMode: () => SuntraceCameraMode;
  getMode: () => SuntraceCameraMode;
  getCameraRoll: () => number;
};

export type ChaseCameraResetLifecycle = {
  requestReset: () => void;
  applyReset: (frame: TrackFrame) => void;
};

export const createChaseCameraResetLifecycle = (
  camera: Pick<SuntraceChaseCamera, "reset">,
): ChaseCameraResetLifecycle => {
  let resetPending = false;
  return {
    requestReset() {
      resetPending = true;
    },
    applyReset(frame) {
      if (!resetPending) {
        return;
      }
      resetPending = false;
      camera.reset(frame);
    },
  };
};

export const createSuntraceChaseCamera = (
  width: number,
  height: number,
): SuntraceChaseCamera => {
  const camera = new THREE.PerspectiveCamera(50, width / height, 0.12, 1_550);
  camera.name = "Asterion chase camera";
  const desiredPosition = new THREE.Vector3();
  const desiredTarget = new THREE.Vector3();
  const chaseAnchor = new THREE.Vector3();
  const smoothedTarget = new THREE.Vector3();
  const lookMatrix = new THREE.Matrix4();
  const lookQuaternion = new THREE.Quaternion();
  const rollQuaternion = new THREE.Quaternion();
  const cameraForward = new THREE.Vector3(0, 0, -1);
  const initializedFrame = new THREE.Vector3();
  const initializedTangent = new THREE.Vector3();
  const initializedRight = new THREE.Vector3();
  const initializedNormal = new THREE.Vector3();
  const frameDisplacement = new THREE.Vector3();
  const relativePosition = new THREE.Vector3();
  const smoothedRoadNormal = new THREE.Vector3();
  const presentationTangent = new THREE.Vector3();
  const presentationRight = new THREE.Vector3();
  const presentationNormal = new THREE.Vector3();
  const loadProfile: ChaseCameraLoadProfile = {
    speedLoad: 0,
    lateralLoad: 0,
    verticalLoad: 0,
    boostLoad: 0,
    sprayLoad: 0,
    cornerLoad: 0,
    anticipation: 0,
    battleOffset: 0,
    battleLoad: 0,
    motionScale: 1,
  };
  const motionController = createChaseCameraMotionController();
  let initialized = false;
  let portrait = width / height < 0.72;
  let mode: SuntraceCameraMode = "chase";
  let modeBlend = 0;
  let smoothedOrbit = 0;
  let smoothedAnticipation = 0;
  let smoothedCornerLoad = 0;
  let smoothedBattleOffset = 0;
  let smoothedBattleLoad = 0;
  let smoothedImpact = 0;
  let smoothedShoulder = 0;
  let smoothedTangentY = 0;
  let roadPhase = 0;
  let impactPhase = 0;
  let cameraRoll = 0;

  const synchronizePresentationFrame = (frame: TrackFrame): void => {
    smoothedRoadNormal.copy(frame.normal);
    smoothedTangentY = finiteClamp(
      frame.tangent.y,
      -MAX_TRACK_GRADE_SINE,
      MAX_TRACK_GRADE_SINE,
    );
    presentationTangent.copy(frame.tangent);
    presentationRight.copy(frame.right);
    presentationNormal.copy(frame.normal);
  };

  const updatePresentationFrame = (
    frame: TrackFrame,
    delta: number,
    highSpeedFactor: number,
    reducedMotion: boolean,
    rebased: boolean,
  ): void => {
    if (rebased) {
      synchronizePresentationFrame(frame);
      return;
    }

    const frameResponse = reducedMotion
      ? 0.3
      : THREE.MathUtils.lerp(0.12, 0.21, highSpeedFactor);
    const frameAlpha = 1 - Math.exp(-delta / frameResponse);
    smoothedTangentY = damp(
      smoothedTangentY,
      finiteClamp(frame.tangent.y, -MAX_TRACK_GRADE_SINE, MAX_TRACK_GRADE_SINE),
      frameResponse,
      delta,
    );
    smoothedRoadNormal.lerp(frame.normal, frameAlpha).normalize();

    presentationTangent.set(frame.tangent.x, 0, frame.tangent.z);
    const horizontalLength = presentationTangent.length();
    if (horizontalLength > 0.0001) {
      const horizontalScale = Math.sqrt(
        Math.max(0, 1 - smoothedTangentY * smoothedTangentY),
      );
      presentationTangent.multiplyScalar(horizontalScale / horizontalLength);
      presentationTangent.y = smoothedTangentY;
    } else {
      presentationTangent.copy(frame.tangent);
    }

    presentationNormal
      .copy(smoothedRoadNormal)
      .addScaledVector(
        presentationTangent,
        -smoothedRoadNormal.dot(presentationTangent),
      );
    if (presentationNormal.lengthSq() < 0.0001) {
      presentationNormal.copy(frame.normal).addScaledVector(
        presentationTangent,
        -frame.normal.dot(presentationTangent),
      );
    }
    presentationNormal.normalize();
    presentationRight
      .crossVectors(presentationNormal, presentationTangent)
      .normalize();
    presentationNormal
      .crossVectors(presentationTangent, presentationRight)
      .normalize();
  };

  const reset = (frame: TrackFrame): void => {
    const distance = portrait ? 8.4 : 5.85;
    const heightOffset = portrait ? 3.9 : 1.92;
    const shoulderOffset = portrait ? 0 : 0.16;
    const targetDistance = portrait ? 7.2 : 8.8;
    const targetHeight = portrait
      ? 0.52
      : 0.54 - (distance + targetDistance) * LANDSCAPE_DOWNWARD_PITCH;
    camera.position
      .copy(frame.position)
      .addScaledVector(frame.tangent, -distance)
      .addScaledVector(frame.normal, heightOffset)
      .addScaledVector(frame.right, shoulderOffset);
    smoothedTarget
      .copy(frame.position)
      .addScaledVector(frame.tangent, targetDistance)
      .addScaledVector(frame.normal, targetHeight);
    lookMatrix.lookAt(camera.position, smoothedTarget, frame.normal);
    camera.quaternion.setFromRotationMatrix(lookMatrix);
    initializedFrame.copy(frame.position);
    initializedTangent.copy(frame.tangent);
    initializedRight.copy(frame.right);
    initializedNormal.copy(frame.normal);
    synchronizePresentationFrame(frame);
    initialized = true;
    smoothedOrbit = 0;
    smoothedAnticipation = 0;
    smoothedCornerLoad = 0;
    smoothedBattleOffset = 0;
    smoothedBattleLoad = 0;
    smoothedImpact = 0;
    smoothedShoulder = shoulderOffset;
    roadPhase = 0;
    impactPhase = 0;
    cameraRoll = 0;
    motionController.reset();
  };

  const mapPointToFrame = (point: THREE.Vector3, frame: TrackFrame): void => {
    relativePosition.copy(point).sub(initializedFrame);
    const rightOffset = relativePosition.dot(initializedRight);
    const heightOffset = relativePosition.dot(initializedNormal);
    const forwardOffset = relativePosition.dot(initializedTangent);
    point
      .copy(frame.position)
      .addScaledVector(frame.right, rightOffset)
      .addScaledVector(frame.normal, heightOffset)
      .addScaledVector(frame.tangent, forwardOffset);
  };

  const advanceReferenceFrame = (frame: TrackFrame): boolean => {
    frameDisplacement.subVectors(frame.position, initializedFrame);
    const rebased = frameDisplacement.lengthSq() > 5_625;
    if (rebased) {
      mapPointToFrame(camera.position, frame);
      mapPointToFrame(smoothedTarget, frame);
    } else {
      camera.position.add(frameDisplacement);
      smoothedTarget.add(frameDisplacement);
    }
    initializedFrame.copy(frame.position);
    initializedTangent.copy(frame.tangent);
    initializedRight.copy(frame.right);
    initializedNormal.copy(frame.normal);
    return rebased;
  };

  return {
    camera,
    update(snapshot, frame, deltaSeconds, reducedMotion) {
      if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
        return;
      }
      let rebased = false;
      if (!initialized) {
        reset(frame);
      } else {
        rebased = advanceReferenceFrame(frame);
      }
      const delta = THREE.MathUtils.clamp(deltaSeconds, 0, 0.25);
      modeBlend = damp(modeBlend, mode === "apex" ? 1 : 0, 0.14, delta);
      const speed = finiteClamp(snapshot.speed, 0, 110);
      const steering = finiteClamp(snapshot.steering, -1, 1);
      const slip = finiteClamp(snapshot.slip, -1.5, 1.5);
      const drift = finiteClamp(snapshot.drift, 0, 1);
      const boost = finiteClamp(snapshot.boost, 0, 1);
      const impact = finiteClamp(snapshot.impact, 0, 1);
      const impactSide = snapshot.impactSide === -1 || snapshot.impactSide === 1
        ? snapshot.impactSide
        : 0;
      const lateralOffset = finiteClamp(snapshot.lateralOffset, -20, 20);
      const speedFactor = THREE.MathUtils.clamp(speed / 86, 0, 1);
      const highSpeedFactor = THREE.MathUtils.smoothstep(speed, 40, 92);
      const velocitySeparation = THREE.MathUtils.smoothstep(speed, 68, 90);
      const driftFactor = THREE.MathUtils.clamp(
        Math.abs(slip) * 1.15 + drift * 0.9,
        0,
        1,
      );
      calculateChaseCameraLoadProfile(snapshot, reducedMotion, loadProfile);
      const motionResponse = motionController.update(
        snapshot,
        delta,
        reducedMotion,
      );
      cameraRoll = motionResponse.rollRadians;
      const motionScale = loadProfile.motionScale;
      updatePresentationFrame(
        frame,
        delta,
        highSpeedFactor,
        reducedMotion,
        rebased,
      );
      smoothedAnticipation = damp(
        smoothedAnticipation,
        loadProfile.anticipation,
        reducedMotion ? 0.3 : 0.14,
        delta
      );
      smoothedCornerLoad = damp(
        smoothedCornerLoad,
        loadProfile.cornerLoad,
        reducedMotion ? 0.34 : 0.18,
        delta
      );
      smoothedBattleOffset = damp(
        smoothedBattleOffset,
        loadProfile.battleOffset,
        reducedMotion ? 0.38 : 0.2,
        delta
      );
      smoothedBattleLoad = damp(
        smoothedBattleLoad,
        loadProfile.battleLoad,
        reducedMotion ? 0.38 : 0.2,
        delta
      );
      smoothedImpact = damp(
        smoothedImpact,
        impact,
        impact > smoothedImpact ? 0.022 : 0.2,
        delta,
      );
      const shoulderTarget = portrait
        ? 0
        : THREE.MathUtils.lerp(0.16, 0.22, modeBlend) +
          highSpeedFactor * 0.14 * motionScale;
      smoothedShoulder = damp(
        smoothedShoulder,
        shoulderTarget,
        reducedMotion ? 0.38 : 0.18,
        delta,
      );
      roadPhase = (roadPhase + delta * (8.6 + speed * 0.13)) % TAU;
      impactPhase = (impactPhase + delta * (27 + speed * 0.09)) % TAU;
      const distance =
        THREE.MathUtils.lerp(
          portrait ? 8.4 : 5.85,
          portrait ? 8.2 : 6.35,
          modeBlend,
        ) +
        speedFactor * THREE.MathUtils.lerp(portrait ? 0.48 : 0.38, portrait ? 0.42 : 0.3, modeBlend) +
        boost * THREE.MathUtils.lerp(0.3, 0.25, modeBlend) * motionScale +
        driftFactor * 0.24 * motionScale +
        smoothedCornerLoad * 0.14 * motionScale +
        smoothedBattleLoad * 0.08 * motionScale;
      const heightOffset =
        THREE.MathUtils.lerp(
          portrait ? 3.9 : 1.92,
          portrait ? 3.45 : 1.72,
          modeBlend,
        ) - speedFactor * THREE.MathUtils.lerp(portrait ? 0.1 : 0.14, 0.12, modeBlend) -
        velocitySeparation * THREE.MathUtils.lerp(portrait ? 0.03 : 0.06, 0.05, modeBlend) -
        smoothedCornerLoad * 0.06 * motionScale;
      const orbitStrength = THREE.MathUtils.lerp(
        portrait ? 0.5 : 1.12,
        portrait ? 0.68 : 1.4,
        modeBlend,
      );
      const orbitTarget =
        (-slip * orbitStrength * (1 + driftFactor * 0.92) -
          steering * 0.18 -
          smoothedAnticipation * 0.38 -
          smoothedBattleOffset * 0.54) *
        motionScale;
      smoothedOrbit = damp(smoothedOrbit, orbitTarget, reducedMotion ? 0.22 : 0.1, delta);
      const impactShake =
        smoothedImpact * (0.12 + speedFactor * 0.15) * motionScale;
      const impactWave = Math.sin(impactPhase);
      const impactSecondaryWave = Math.sin(impactPhase * 1.61 + 0.8);
      chaseAnchor
        .copy(frame.position)
        .addScaledVector(frame.right, -lateralOffset * 0.12);
      desiredPosition
        .copy(chaseAnchor)
        .addScaledVector(presentationTangent, -distance)
        .addScaledVector(presentationNormal, heightOffset)
        .addScaledVector(
          presentationRight,
          smoothedShoulder + smoothedOrbit + motionResponse.lateralOffset,
        )
        .addScaledVector(presentationTangent, -motionResponse.boostLag);
      const roadHeave =
        (Math.sin(roadPhase) * 0.62 + Math.sin(roadPhase * 1.73 + 0.9) * 0.38) *
        (0.008 + loadProfile.speedLoad * 0.046 + smoothedCornerLoad * 0.02) *
        motionScale;
      const roadSway =
        (Math.sin(roadPhase * 0.83 + 1.2) * 0.66 +
          Math.sin(roadPhase * 2.07) * 0.34) *
        (0.004 + highSpeedFactor * 0.022) *
        motionScale;
      desiredPosition
        .addScaledVector(presentationNormal, roadHeave)
        .addScaledVector(presentationRight, roadSway)
        .addScaledVector(
          presentationTangent,
          -smoothedImpact * (0.05 + speedFactor * 0.08) * motionScale,
        );
      desiredPosition
        .addScaledVector(
          presentationRight,
          impactWave * impactShake +
            impactSide * smoothedImpact * 0.07 * motionScale,
        )
        .addScaledVector(
          presentationNormal,
          impactSecondaryWave * impactShake * 0.72,
        );
      const targetDistance =
        THREE.MathUtils.lerp(
          portrait ? 7.2 : 8.8,
          portrait ? 6.7 : 9.25,
          modeBlend,
        ) +
        speedFactor * THREE.MathUtils.lerp(portrait ? 3.5 : 4.45, 3.8, modeBlend) +
        velocitySeparation * THREE.MathUtils.lerp(portrait ? 1.25 : 1.75, 1.5, modeBlend) +
        smoothedCornerLoad * 0.5 * motionScale;
      const targetHeight =
        THREE.MathUtils.lerp(
          portrait ? 0.52 : 0.46,
          portrait ? 0.48 : 0.55,
          modeBlend,
        ) -
        (portrait ? 0 : (distance + targetDistance) * LANDSCAPE_DOWNWARD_PITCH) +
        motionResponse.targetHeight;
      desiredTarget
        .copy(frame.position)
        .addScaledVector(presentationTangent, targetDistance)
        .addScaledVector(presentationNormal, targetHeight)
        .addScaledVector(
          presentationRight,
          -slip *
            THREE.MathUtils.lerp(portrait ? 0.82 : 1.2, portrait ? 1.02 : 1.58, modeBlend) *
            motionScale +
            smoothedAnticipation * 0.78 * motionScale +
            smoothedBattleOffset * motionScale +
            impactWave * impactShake * 0.2,
        );
      const positionResponse =
        0.105 + driftFactor * 0.04 + (reducedMotion ? 0.08 : 0);
      const targetResponse =
        0.075 + driftFactor * 0.022 + (reducedMotion ? 0.06 : 0);
      const positionAlpha = 1 - Math.exp(-delta / positionResponse);
      const targetAlpha = 1 - Math.exp(-delta / targetResponse);
      camera.position.lerp(desiredPosition, positionAlpha);
      smoothedTarget.lerp(desiredTarget, targetAlpha);
      lookMatrix.lookAt(camera.position, smoothedTarget, presentationNormal);
      lookQuaternion.setFromRotationMatrix(lookMatrix);
      rollQuaternion.setFromAxisAngle(
        cameraForward,
        motionResponse.rollRadians,
      );
      camera.quaternion.copy(lookQuaternion).multiply(rollQuaternion);
      const speedFov =
        11 * THREE.MathUtils.smoothstep(speed, 18, 62) +
        6.8 * THREE.MathUtils.smoothstep(speed, 50, 90);
      const targetFov =
        THREE.MathUtils.lerp(portrait ? 57 : 49.8, portrait ? 55 : 49, modeBlend) +
        speedFov *
          THREE.MathUtils.lerp(1, 0.9, modeBlend) *
          (reducedMotion ? 0.34 : 1) +
        highSpeedFactor * 1.1 * motionScale +
        boost *
          THREE.MathUtils.lerp(portrait ? 2.2 : 2, 1.8, modeBlend) *
          motionScale +
        driftFactor * THREE.MathUtils.lerp(1.2, 1.5, modeBlend) * motionScale +
        (loadProfile.speedLoad * 0.52 + smoothedCornerLoad * 0.62) * motionScale +
        smoothedImpact * 1.8 * motionScale;
      if (!Number.isFinite(camera.fov)) {
        camera.fov = portrait ? 57 : 49.8;
      }
      camera.fov = damp(camera.fov, targetFov, 0.16, delta);
      camera.updateProjectionMatrix();
    },
    resize(nextWidth, nextHeight) {
      camera.aspect = nextWidth / nextHeight;
      portrait = camera.aspect < 0.72;
      camera.updateProjectionMatrix();
    },
    reset,
    cycleMode() {
      motionController.reset();
      mode = mode === "chase" ? "apex" : "chase";
      return mode;
    },
    getMode() {
      return mode;
    },
    getCameraRoll() {
      return cameraRoll;
    },
  };
};
